import {Component, Inject, OnInit, ViewEncapsulation} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {ActivatedRoute, ActivationEnd, NavigationEnd, NavigationStart, Router, RouterOutlet} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { Logger } from 'src/util/logger';
import { AppConstants } from './AppConstants';
import {AuthenticationInfo, AuthenticationInfoService, SessionInfo} from './authentication-info.service';
import { ParamsService } from './params.service';
import {Observable, Subscription} from 'rxjs';
import {APP_BASE_HREF, DOCUMENT, PlatformLocation} from '@angular/common';
import {AcceptInvitationChallenge, AuthenticationChallenge, User} from '../gen';
import {LocaleService} from './locale.service';
import {routeTransition} from './animations';
import {getApiBasePath} from '../api/real-api/api-base-path';
import {CustomAcceptInvitationAuthenticationChallenge} from '../CustomAcceptInvitationAuthenticationChallenge';
import {StandaloneComponent} from './standalone.component';

const _ = (s) => s;
// Could not figure out how to use the variables with this setup, so I added the logic here.
function buildWindowTitle(genericpart: string, componentpart?: string): string {
    return componentpart ? `${componentpart} | ${genericpart}` : genericpart;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [ routeTransition ],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  public static resolveUserName(user: User, translate: TranslateService, partialSession: boolean): string|any {
    let retVal: string|any;
    if (user) {
      if (user.preferredUsername) {
        retVal = user.preferredUsername;
      } else if (user.firstName) {
        retVal = user.firstName;
        if (user.lastName) {
          retVal += ' ' + user.lastName;
        }
      } else if (user.lastName) {
        retVal = user.lastName;
      } else if (user.nickname) {
        retVal = user.nickname;
      } else {
        retVal = translate.instant(_('app.user-name-label.unknown'));
      }
    } else if (partialSession) {
      retVal = translate.instant(_('app.user-name-label.unknown'));
    } else {
      retVal = translate.instant(_('app.user-name-label.not-signed-in'));
    }
    //
    return retVal;
  }
  sessionPanelOpen = false;
  /** Url where to redirect after login is complete */
  nextUrl: string;

  /** Session info */
  sessionInfo: SessionInfo;

  /** Initial route URL */
  initialRouteUrl: string;

  /** Processing state (redirecting out of this application) */
  processing: boolean;

  private userAuthenticated: boolean;
  private initialRouteReached = false;
  profile: User;

  supportedLanguages: any[] = [
    { value: 'en', label: 'English' },
    { value: 'fi', label: 'Suomi' },
  ];

  private routeUrlSubscription: Subscription;
  private nextUrlSubscription: Subscription;
  private localeSupportDismissed = false;
  private loginBeforeNextDismissed = false;
  private activeStandaloneComponent: StandaloneComponent;

  constructor(
    private authnInfoService: AuthenticationInfoService,
    private logger: Logger,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private titleService: Title,
    private meta: Meta,
    private paramsService: ParamsService,
    @Inject(DOCUMENT) private _document: any,
    @Inject(APP_BASE_HREF) private baseHref: string,
    private localeService: LocaleService,
    private platformLocation: PlatformLocation) {
    // these calls have to include "this." or else the resource extractor will fail to identify
    this.translate.get('app.window-title', {service_name: AppConstants.SERVICE_NAME}).subscribe(res => {
      titleService.setTitle(buildWindowTitle(res));
    });
    this.translate.onLangChange.subscribe(next => {

      if (this.activeStandaloneComponent) {
        this.activeStandaloneComponent.resolveWindowTitlePart().subscribe(part => {
          translate.get('app.window-title', {service_name: AppConstants.SERVICE_NAME}).subscribe(res => {
            titleService.setTitle(buildWindowTitle(res, part));
          });
        });
      } else {
        translate.get('app.window-title', {service_name: AppConstants.SERVICE_NAME}).subscribe(res => {
          titleService.setTitle(buildWindowTitle(res));
        });
      }
    });
    this.translate.get('app.window-description', {service_name: AppConstants.SERVICE_NAME}).subscribe(res => {
      meta.addTag({ name: 'Description', content: res });
    });
    this.translate.onLangChange.subscribe(next => {
      this._document.documentElement.lang = next.lang;
      this.logger.debug('Language: %s', next.lang);
      translate.get('app.window-description', {service_name: AppConstants.SERVICE_NAME}).subscribe(res => {
        meta.addTag({ name: 'Description', content: res });
      });
    });
  }
  ngOnInit() {
    this.initialize();
    if (this.translate.currentLang && this.localeService.getIsAvailable()) {
      this._document.documentElement.lang = this.translate.currentLang;
    } else if (this.translate.defaultLang) {
      this._document.documentElement.lang = this.translate.defaultLang;
    } else {
      throw new Error('AppComponent.ngOnInit: no supported languages found to set as document language.');
    }
    // trigger profile load
    this.authnInfoService.getUser().subscribe((resp) => {
      // once completed subscribe to profile changes
      this.authnInfoService.getProfileSubscription().subscribe(response => { this.profile = response; });
    }, (err) => {
      // once failed subscribe to profile changes and hope for the best :D
      this.authnInfoService.getProfileSubscription().subscribe(response => { this.profile = response; });
    });

  }
  onRouteActivated(c: StandaloneComponent) {
    this.activeStandaloneComponent = c;
    c.resolveWindowTitlePart().subscribe(part => {
      this.translate.get('app.window-title',
        {service_name: AppConstants.SERVICE_NAME}
      ).subscribe(res => {
        this.titleService.setTitle(buildWindowTitle(res, part));
      });

    });
    if (!this.initialRouteReached) {
      // detect when the very first route has been reached.
      const temp = c.initialized.subscribe((f: boolean) => {
        this.initialRouteReached = true;
        const t = this.authnInfoService.sessionInfo.subscribe(sessnInfo => {
          // if no tasks are in progress, continue forward
          if (sessnInfo.tasksInProgress.length === 0) {
            this.continueAfterInitialized();
          }
        });
        t.unsubscribe();
        temp.unsubscribe();
      });
    }
  }
  resolveNextLabel(next: string, translate: TranslateService): string {
    let retVal = '';
    if (next) {
      if (next.match(/^(\/[a-z]{2}(-[a-z]{2})?)?\/validate\/.*/)) {
        retVal = translate.instant(_('app.next-label.validate'));
      } else {
        retVal = translate.instant(_('app.next-label.other'));
      }
    }
    return retVal;
  }
  openSessionPanel() {
    if (!this.sessionPanelOpen) {
      setTimeout(() => {
        this.sessionPanelOpen = true;
      }, 1);
    }
  }
  prepareRouteTransition(o: RouterOutlet): any {
    let retVal: any = 'APP_INIT';
    if (this.isInitialized()) {
      if (o && o.isActivated && o.activatedRouteData && o.activatedRouteData['animation']) {
        retVal = o.activatedRouteData['animation'];
      } else if (0 && o.isActivated && o.activatedRoute) {
        retVal = o.activatedRoute;
      }
    }
    //
    return retVal;
  }
  showLoginBeforeNext(): boolean {
    return !this.showLocaleNotSupported() && this.nextUrl && !this.loginBeforeNextDismissed && this.router.url.includes('/login');
  }
  dismissLoginBeforeNext() {
    this.loginBeforeNextDismissed = true;
  }
  showLocaleNotSupported(): boolean {
    return !this.localeService.getIsAvailable() && !this.localeSupportDismissed;
  }
  dismissLocaleNotSupported() {
    this.localeSupportDismissed = true;
  }
  resolveLoggedInUserName(): string|any {
    return AppComponent.resolveUserName(this.profile, this.translate, (this.isUserAuthenticated() && !this.isUserFullyAuthenticated()));
  }
  // noinspection JSMethodCanBeStatic
  getAppConstant(name: string): any {
    return AppConstants[name];
  }
  signOut() {
    if (this.authnInfoService &&
      this.authnInfoService.sessionInfo &&
      this.authnInfoService.sessionInfo.getValue().tasksInProgress &&
      this.authnInfoService.sessionInfo.getValue().tasksInProgress.length) {
      // cancel whatever task may be in progress as the automated cancel from ngOnDestory fires too late
      // for logout calls, causing the ui to think were still logging in
      this.authnInfoService.cancelTask(this.authnInfoService.sessionInfo.getValue().tasksInProgress[0]);
    }
    this.router.navigate([AppConstants.PATH_LOGOUT], {
      relativeTo: this.activatedRoute,
    });
  }
  changeLanguage(lang: string) {
    const url = window.location.href;
    if (this.translate.currentLang && url.indexOf(this.translate.currentLang) >= 0) {
      window.location.href = url.replace(this.translate.currentLang, lang);
    } else {
      window.location.href = url.replace(window.location.origin, window.location.origin + '/' + lang);
    }
  }
  isSelectedLanguage(lang: string): boolean {
    let retVal = false;
    const cur = this.translate.currentLang || this.translate.defaultLang;
    if (cur === lang) {
      retVal = true;
    } else if (lang.indexOf('-') > 0 && cur === lang.split('-')[0]) {
      retVal = true;
    } else if (cur.indexOf('-') > 0 && lang === cur.split('-')[0]) {
      retVal = true;
    }
    return retVal;
  }

  getHomeLink(): string {
    let retVal: string;
    if (this.isUserFullyAuthenticated()) {
      retVal = AppConstants.PATH_PROFILE;
    } else {
      retVal = AppConstants.PATH_LOGIN;
    }
    return retVal;
  }

  isUserAuthenticated(): boolean {
    return this.isUserPartiallyAuthenticated() || this.isUserFullyAuthenticated();
  }

  private isUserPartiallyAuthenticated(): boolean {
    return this.sessionInfo && this.sessionInfo.hasSession;
  }
  private isUserFullyAuthenticated(): boolean {
    return this.sessionInfo && this.sessionInfo.fullyAuthenticated;
  }
  private subscribeNextUrl() {
    this.nextUrlSubscription = this.paramsService.params.subscribe(params => {
      if (params !== undefined && params[AppConstants.QP_NEXT] !== undefined) {
        this.nextUrl = params[AppConstants.QP_NEXT];
        if (this.nextUrlSubscription) {
          this.nextUrlSubscription.unsubscribe();
          this.nextUrlSubscription = null;
        }
        this.continueAfterInitialized();
      }
    });
    this.paramsService.getNextUrl();
  }

  private subscribeAuthenticationInfo() {
    this.authnInfoService.sessionInfo.subscribe(sessnInfo => {
      this.logger.debug('sessioninfo: %o', sessnInfo);
      if (this.sessionInfo && sessnInfo.tasksInProgress.length === 0) {
        // Only react when there are no session state altering tasks in progress.
        const oldSessionInfo: SessionInfo = this.sessionInfo;
        const authInfo: AuthenticationInfo = this.authnInfoService.authenticationInfo.getValue();
        this.setSessionInfo(sessnInfo);
        this.userAuthenticated = this.sessionInfo.hasSession;
        if (this.userAuthenticated &&
            !this.sessionInfo.fullyAuthenticated) {
          // login not completed, find next challenge and navigate to matching component
          // authInfo should have reuired Authentications to handle
          const nextChallenge = authInfo.requiredAuthentications[0];
          this.handleChallenge(nextChallenge);
        } else if (this.userAuthenticated &&
                   this.sessionInfo.fullyAuthenticated &&
                   !this.sessionInfo.optionalsHandled) {
          // Login completed, but optional steps remain. find the next challenge and navigate to matching component.
          // authInfo should have optional Authentications to handle
          const nextChallenge = authInfo.optionalAuthentications[0];
          this.handleChallenge(nextChallenge);
        } else if (this.sessionInfo.fullyAuthenticated &&
                   (!oldSessionInfo.fullyAuthenticated || (this.sessionInfo.optionalsHandled && !oldSessionInfo.optionalsHandled))) {
          // Login completed and optionals handled
          this.continueAfterAuthenticated();
        } else if (oldSessionInfo.hasSession === true && !this.sessionInfo.hasSession) {
          this.continueAfterDeauthenticated();
        }
      }
    });
    this.logger.debug('Updating authentication status from app.component, no user defined');
    this.authnInfoService.fetchAuthenticationInfo(undefined, (a: AuthenticationInfo, s: SessionInfo) => {
      this.setSessionInfo(s);
      this.continueAfterInitialized();
    });
  }
  private handleChallenge(c: AuthenticationChallenge) {
    if (!c) {
      throw new Error('AppComponent.handleChallenge: Challenge not defined');
    } else {
      if (c.authenticationMethod === AppConstants.AC_AM_VERIFY_EMAIL) {
        const qp = {};
        if (this.nextUrl) {
          const splitted = this.nextUrl.split('?');
          let tmp = this.baseHref;
          if (tmp && tmp.endsWith('/')) {
            tmp = tmp.substr(0, tmp.length - 1);
          }
          if (splitted[0] === tmp + AppConstants.PATH_VALIDATE_EMAIL) {
            this.nextUrl = null;
            qp[AppConstants.QP_NEXT] = null;
          }
        }
        this.router.navigate([AppConstants.PATH_VALIDATE_EMAIL], {
          relativeTo: this.activatedRoute,
          queryParams: qp,
          queryParamsHandling: 'merge'
        });
      } else if (c.authenticationMethod === AppConstants.AC_AM_INVITATION) {
        const ic = c as AcceptInvitationChallenge;
        this.router.navigate([AppConstants.PATH_JOIN], {
          relativeTo: this.activatedRoute,
          queryParams: {
            [AppConstants.QP_EMAIL]: ic.userName,
            [AppConstants.QP_INVITATION_ID]: ic.invitationId,
          },
          queryParamsHandling: 'merge'
        });
      } else if (c.authenticationMethod === AppConstants.AC_AM_INVITATION_ACCEPTED) {
        const ic = c as CustomAcceptInvitationAuthenticationChallenge;
        this.router.navigate([AppConstants.PATH_JOIN], {
          relativeTo: this.activatedRoute,
          queryParams: {
            [AppConstants.QP_EMAIL]: ic.email,
            [AppConstants.QP_INVITATION_TOKEN]: ic.token,
            [AppConstants.QP_HANDLE_CHALLENGE]: AppConstants.AC_AM_INVITATION_ACCEPTED,
            [AppConstants.QP_REQUIRE_CHALLENGE]: null,
          },
          queryParamsHandling: 'merge'
        });
      } else {
        throw new Error('AppComponent.handleChallenge: unsupported challenge: ' + c.authenticationMethod);
      }
    }
  }
  setSessionInfo(s: SessionInfo) {
    this.sessionInfo = s;
  }
  private subscribeInitialRouteUrl() {
    this.routeUrlSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
      this.logger.debug('Initial route URL: %s', this.router.url);
      this.initialRouteUrl = this.router.url.replace(/#.*$/, '');
      if (this.routeUrlSubscription) {
        this.routeUrlSubscription.unsubscribe();
        this.routeUrlSubscription = null;
      }
      this.continueAfterInitialized();
    });
  }

  private getInitialRoutePath() {
    return this.initialRouteUrl ? this.initialRouteUrl.split('?')[0] : this.initialRouteUrl;
  }

  private initialize() {
    this.subscribeInitialRouteUrl();
    this.subscribeNextUrl();
    this.subscribeAuthenticationInfo();
  }

  public isInitialized(): boolean {
    return this.sessionInfo !== undefined && this.nextUrl !== undefined && this.initialRouteUrl !== undefined && this.initialRouteReached;
  }

  showUI(): boolean {
    return this.sessionInfo !== undefined && this.nextUrl !== undefined && this.initialRouteUrl !== undefined;
  }

  public isIEorEDGE(): boolean {
    const retVal: boolean = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
    return retVal;
  }
  private continueAfterDeauthenticated() {
    if (this.nextUrl) {
      this.processing = true;
      this.logger.info('Redirecting deauthenticated user to %s', this.nextUrl);
      setTimeout(() => {
        this.handleNextUrl(this.nextUrl);
      }, 1);
    } else {
      this.router.navigate([AppConstants.PATH_LOGIN], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge'
      });
    }
  }
  private handleNextUrl(url: string) {
    if (url) {
      if (url.startsWith('/') &&
          !url.startsWith('//')) {
        if (!AppConstants.PATHS.filter((val) => val !== AppConstants.PATH_HOME && url.startsWith(val)).length) {
          window.location.href = new URL(url, getApiBasePath(this.platformLocation)).href;
        } else {
          const params = {};
          let hash = undefined;
          let noParamUrl = url;
          if (url.indexOf('#') >= 0) {
            const splitted = url.split('#');
            hash = splitted[1];
            noParamUrl = splitted[0];
          }
          if (noParamUrl.indexOf('?') >= 0) {
            const splitted = noParamUrl.split('?');
            noParamUrl = splitted[0];
            const p = splitted[1].split('&');
            for (let i = 0; i < p.length; i += 1) {
              const t = p[i].split('=');
              params[t[0]] = t[1];
            }
          }
          this.router.navigate([noParamUrl], {
            relativeTo: this.activatedRoute,
            queryParams: params,
            fragment: hash,
          }).then(() => {
            this.processing = false;
          });
        }
      } else if (!url.startsWith('javascript:')) {
        window.location.href = url;
      }
    }
  }
  private continueAfterAuthenticated() {
    this.paramsService.getNextUrl().then(nextUrl => {
      // Cant user this.nextUrl here as it does not always exist at the time of evaluation.
      // Figuring out why is way too much work compared to just getting it.
      if (nextUrl) {
        this.processing = true;
        this.logger.info('Redirecting authenticated user to %s', nextUrl);
        setTimeout(() => {
          this.handleNextUrl(nextUrl);
        }, 1);
      } else if (this.getInitialRoutePath() === AppConstants.PATH_HOME
        || this.getInitialRoutePath() === AppConstants.PATH_LOGIN
        || this.getInitialRoutePath() === AppConstants.PATH_REGISTER
        || this.getInitialRoutePath() === AppConstants.PATH_RESET_PASSWORD
        || this.getInitialRoutePath() === AppConstants.PATH_FORGOT_PASSWORD
        || this.getInitialRoutePath() === AppConstants.PATH_VALIDATE_EMAIL
        || this.getInitialRoutePath() === AppConstants.PATH_JOIN) {
        /**
         * Should target all entry points that will eventually provide a login state once flows completed.
         */
        this.logger.debug('Routing authenticated user to %s', AppConstants.PATH_PROFILE);
        this.router.navigate([AppConstants.PATH_PROFILE], {
          relativeTo: this.activatedRoute,
        });
        this.initialRouteUrl = AppConstants.PATH_HOME;
      } else if (this.getInitialRoutePath() && this.getInitialRoutePath() !== AppConstants.PATH_HOME) {
        this.router.navigate([this.getInitialRoutePath()], {
          relativeTo: this.activatedRoute,
        });
        this.initialRouteUrl = AppConstants.PATH_HOME;
      } else {
        this.router.navigate([AppConstants.PATH_PROFILE], {
          relativeTo: this.activatedRoute,
        });
        this.logger.debug('User authenticated. Initial route was %s, going to profile page', this.initialRouteUrl);
      }
    });
  }

  private continueAfterInitialized() {
    if (this.isInitialized() && (!this.sessionInfo || !this.sessionInfo.tasksInProgress || !this.sessionInfo.tasksInProgress.length)) {
      if (this.isUserFullyAuthenticated()) {
        this.continueAfterAuthenticated();
      } else {
        const initialRoutePath = this.getInitialRoutePath();
        if (initialRoutePath === AppConstants.PATH_HOME) {
          this.logger.debug('Navigating from index to %s', AppConstants.PATH_LOGIN);
          this.router.navigate([AppConstants.PATH_LOGIN], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge',
          });
        } else if (initialRoutePath === AppConstants.PATH_PROFILE
          || initialRoutePath === AppConstants.PATH_CHANGE_PASSWORD
          || initialRoutePath === AppConstants.PATH_CONFIGURE_TOTP
          || initialRoutePath === AppConstants.PATH_EDIT_PROFILE
          || (initialRoutePath === AppConstants.PATH_VALIDATE_EMAIL && !this.isUserPartiallyAuthenticated())) {
          this.logger.debug('Navigating from %s to %s', initialRoutePath, AppConstants.PATH_LOGIN);
          const baseUrl = this.baseHref && this.baseHref.endsWith('/')
            ? this.baseHref.substring(0, this.baseHref.length - 1)
            : this.baseHref;
          const nextUrlAfterLogin = baseUrl + this.initialRouteUrl;
          this.nextUrl = nextUrlAfterLogin;
          this.router.navigate([AppConstants.PATH_LOGIN], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge',
            queryParams: {
              next: nextUrlAfterLogin // the "next" param value is used only if reloading the page
            }
          });
        } else if (initialRoutePath === AppConstants.PATH_LOGOUT && !this.isUserAuthenticated()) {
          if (this.nextUrl) {
            this.logger.info('Redirecting deauthenticated user to %s', this.nextUrl);
            setTimeout(() => {
              window.location.href = this.nextUrl;
            }, 1);
          } else {
            this.router.navigate([AppConstants.PATH_LOGIN], {
              relativeTo: this.activatedRoute,
            });
          }
        }
      }
    }
  }
}
